import { Button, Icon } from "../../../components/design";
import useTranslator from "../../../hooks/useTranslator";
import styles from "../../onboarding/authenticateYourId/authenticateYourId.module.css";

interface GuideScreenProps {
  docType?: string;
  requiresBack: boolean;
  frontCaptured: boolean;
  onStart: () => void;
}

const getTranslatedText = (
  _t: (key: string, context?: string) => string,
  key: string,
  fallback: string
) => {
  return _t(key);
};

const getHeroIcon = (docType?: string, frontCaptured?: boolean) => {
  if (docType === "passport") {
    return "/images/passport-icon.svg";
  }
  if (frontCaptured) {
    return "/images/docs-back-upload.svg";
  }
  if (docType === "residence_permit") {
    return "/images/resident.svg";
  }
  return "/images/idcard-icon.svg";
};

const GuideScreen = ({
  docType,
  requiresBack,
  frontCaptured,
  onStart,
}: GuideScreenProps) => {
  const { _t } = useTranslator();
  const HeroIcon = getHeroIcon(docType, frontCaptured);

  const title = (() => {
    if (docType === "passport") {
      return getTranslatedText(
        _t,
        "photographYourPassport",
        "Photograph your passport"
      );
    }
    if (docType === "residence_permit") {
      return getTranslatedText(
        _t,
        "photographYourResidencePermit",
        "Photograph your residence permit"
      );
    }
    return getTranslatedText(
      _t,
      "photographYourNationalIdentityCard",
      "Photograph your ID"
    );
  })();

  const description = requiresBack
    ? getTranslatedText(
        _t,
        "twoPhotosOneForEachSide",
        "You'll need to take two photos, one for each side"
      )
    : getTranslatedText(
        _t,
        "passportPhotoPageHint",
        "We need the photo page, complete and legible"
      );

  const ctaLabel = frontCaptured
    ? getTranslatedText(_t, "takePhotoOfBack", "Take photo of the back")
    : requiresBack
      ? getTranslatedText(_t, "takePhotoOfFront", "Take photo of the front")
      : getTranslatedText(_t, "takePhoto", "Take photo");

  return (
    <div className="mobile-capture-screen">
      <div className="mobile-capture-guide">
        <div className="mobile-capture-guide-content">
          <div className="mobile-capture-hero" aria-hidden="true">
            <img
              src={HeroIcon}
              className={styles.customKycUploadHeroIcon}
              alt="Hero Icon"
            />
          </div>

          <div className="mobile-capture-guide-copy">
            <h3>{title}</h3>
            <p>{description}</p>
          </div>

          {requiresBack && (
            <div
              className="mobile-capture-step-list"
              aria-label={getTranslatedText(
                _t,
                "twoPhotosRequired",
                "Two photos required"
              )}
            >
              <div
                className={`mobile-capture-step-item ${
                  frontCaptured ? "completed" : "active"
                }`}
              >
                <span className="mobile-capture-step-number" aria-hidden="true">
                  {frontCaptured ? "✓" : "1"}
                </span>
                <span className="mobile-capture-step-label">
                  {_t("documentFront")}
                </span>
              </div>
              <div
                className={`mobile-capture-step-item ${
                  frontCaptured ? "active" : ""
                }`}
              >
                <span className="mobile-capture-step-number" aria-hidden="true">
                  2
                </span>
                <span className="mobile-capture-step-label">
                  {_t("documentBack")}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="mobile-capture-guide-footer">
          <Button
            size="lg"
            block
            iconStart={<Icon name="camera" />}
            onClick={onStart}
          >
            {ctaLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GuideScreen;
